import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg, inr, fmtDate, today } from "@/lib/api";
import { PageHeader, StatCard, SearchBox, TableWrap, Empty, th, td, tr } from "@/components/common";

function podState(b) {
  if (!(b.pod_files || []).length) return { key: "missing", label: "POD Missing", cls: "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-400" };
  if (b.pod_status === "not_ok") return { key: "not_ok", label: "POD NOT OK", cls: "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-400" };
  return { key: "ok", label: "POD OK", cls: "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-400" };
}

export default function PodRecords() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  useEffect(() => { api.get("/bookings", { params: { status: "Delivered" } }).then((r) => setRows(r.data)); }, []);

  const savePodDate = async (bid, value) => {
    const prev = rows;
    setRows(rows.map((b) => (b.id === bid ? { ...b, pod_submitted_date: value } : b)));
    try { await api.patch(`/bookings/${bid}/pod-submitted`, { pod_submitted_date: value }); toast.success("POD submission date saved"); }
    catch (e) { setRows(prev); toast.error(errMsg(e)); }
  };

  const list = useMemo(() => {
    const ql = q.toLowerCase();
    return rows
      .filter((b) => !ql || `${b.lr_number} ${b.customer_name} ${b.vehicle_number || ""} ${b.driver_name || ""} ${b.pickup_location} ${b.drop_location}`.toLowerCase().includes(ql))
      .filter((b) => filter === "all" || podState(b).key === filter);
  }, [rows, q, filter]);

  const okCount = rows.filter((b) => podState(b).key === "ok").length;
  const issueCount = rows.filter((b) => podState(b).key === "not_ok").length;
  const missingCount = rows.filter((b) => podState(b).key === "missing").length;
  const shortageTotal = rows.filter((b) => podState(b).key === "not_ok").reduce((s, b) => s + (b.claim_amount || 0), 0);

  const download = () => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [["LR No", "Customer", "Route", "Vehicle", "Driver", "Loading Point On", "POD Submitted On", "POD Status", "Shortage Qty", "Shortage Detail", "Shortage Value (Rs)"]];
    for (const b of list) {
      lines.push([b.lr_number, b.customer_name, `${b.pickup_location} -> ${b.drop_location}`, b.vehicle_number || "-", b.driver_name || "-",
        b.delivered_at ? fmtDate(b.delivered_at) : "-", b.pod_submitted_date ? fmtDate(b.pod_submitted_date) : "Entry pending",
        podState(b).label, b.shortage_qty || "", b.shortage_desc || "", b.claim_amount || 0]);
    }
    const blob = new Blob(["﻿" + lines.map((r) => r.map(esc).join(",")).join("\n")], { type: "text/csv;charset=utf-8" });
    const names = { all: "All", ok: "POD_OK", not_ok: "POD_NOT_OK", missing: "POD_Missing" };
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `POD_Records_${names[filter]}_${today()}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success(`Downloaded ${list.length} records`);
  };

  return (
    <div data-testid="pod-records-page">
      <PageHeader title="POD Records" subtitle="Delivery POD register — submission date, verification status and shortage value for every completed trip" />
      <div className="mb-5 grid grid-cols-2 gap-4 lg:grid-cols-5">
        <StatCard testid="pod-stat-total" label="Loading Point Trips" value={rows.length} />
        <StatCard testid="pod-stat-ok" label="POD Verified OK" value={okCount} accent="text-emerald-600" />
        <StatCard testid="pod-stat-issues" label="POD NOT OK" value={issueCount} accent={issueCount ? "text-rose-600" : ""} />
        <StatCard testid="pod-stat-missing" label="POD Missing" value={missingCount} accent={missingCount ? "text-amber-600" : ""} />
        <StatCard testid="pod-stat-shortage" label="Total Shortage Value" value={inr(shortageTotal)} accent={shortageTotal ? "text-rose-600" : ""} />
      </div>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <SearchBox testid="pod-search-input" value={q} onChange={setQ} placeholder="Search LR, customer, vehicle, driver…" />
        <div className="flex items-center gap-2">
          <div className="flex gap-1.5" data-testid="pod-filter-chips">
            {[["all", "All"], ["ok", "POD OK"], ["not_ok", "NOT OK"], ["missing", "Missing"]].map(([k, l]) => (
              <button key={k} data-testid={`pod-filter-${k}`} onClick={() => setFilter(k)}
                className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${filter === k ? "border-primary bg-primary text-primary-foreground" : "text-muted-foreground hover:border-ring"}`}>{l}</button>
            ))}
          </div>
          <Button variant="outline" size="sm" data-testid="pod-download-btn" onClick={download} disabled={!list.length} title="Download the filtered records as an Excel/CSV sheet">
            <Download className="mr-1 h-4 w-4" /> Download Sheet
          </Button>
        </div>
      </div>
      <TableWrap>
        <thead><tr>{["LR No", "Route", "Vehicle / Driver", "Loading Point On", "POD Submitted", "POD Status", "Shortage", "Shortage Value"].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
        <tbody>
          {list.map((b) => {
            const st = podState(b);
            return (
              <tr key={b.id} className={tr} data-testid={`pod-row-${b.lr_number}`}>
                <td className={td}><Link to={`/bookings/${b.id}`} className="font-mono font-semibold text-primary hover:underline">{b.lr_number}</Link><div className="text-xs text-muted-foreground">{b.customer_name}</div></td>
                <td className={td}>{b.pickup_location} → {b.drop_location}</td>
                <td className={td}><span className="font-mono text-xs">{b.vehicle_number || "-"}</span><div className="text-xs text-muted-foreground">{b.driver_name || "-"}</div></td>
                <td className={td}>{b.delivered_at ? fmtDate(b.delivered_at) : "-"}</td>
                <td className={td} data-testid={`pod-submitted-${b.lr_number}`}>
                  <input type="date" data-testid={`pod-submitted-input-${b.lr_number}`} value={b.pod_submitted_date || ""}
                    onChange={(e) => savePodDate(b.id, e.target.value)}
                    className="rounded-md border bg-transparent px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-ring" />
                  {!b.pod_submitted_date && <div className="mt-0.5 text-[10px] font-medium text-amber-600">Entry pending</div>}
                </td>
                <td className={td}><span data-testid={`pod-status-${b.lr_number}`} className={`rounded-full border px-2 py-0.5 text-xs font-bold ${st.cls}`}>{st.label}</span></td>
                <td className={td}>{b.shortage_qty ? <>{b.shortage_qty}{b.shortage_desc && <div className="max-w-40 truncate text-xs text-muted-foreground">{b.shortage_desc}</div>}</> : "—"}</td>
                <td className={`${td} amount font-semibold ${b.claim_amount ? "text-rose-600" : ""}`} data-testid={`pod-shortage-value-${b.lr_number}`}>{b.claim_amount ? inr(b.claim_amount) : "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </TableWrap>
      {list.length === 0 && <Empty testid="pod-records-empty" text="No delivered trips match this filter" />}
    </div>
  );
}
